import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
} from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";

import { FormSectionDescriptor } from "../models/form-section-descriptor";
import { FormFieldConfig } from "../models/form-field-config";
import { FormControl } from "../models/form-control";
import { IFormField } from "../interfaces/iform-field";
import * as _ from "lodash";
import { Observable } from "rxjs";
import { IValueAccessor } from "../interfaces/ivalue-accessor";
import { TableAction } from "../../../../../utils/types/shared/table-action";
import { FormControlType, FormControlSize } from "../enums";
import { FieldDescriptor } from "../models/field-descriptor";
import { FormFieldSize } from "../decorators/form-field-size.decorator";
import { FieldSizeDescriptor } from "../models/field-size-descriptor";
import { FieldOptionsDescriptor } from "../models/field-options-descriptor";
import { IdValue } from "../models/id-value";
import { PropertyChange } from "../../../../../utils/types/shared/property-change";

declare let $: any;

@Component({
    selector: "wtf-form",
    templateUrl: "./form.component.html",
    styleUrls: ["./form.component.scss"],
})
export class FormComponent implements OnInit, AfterViewInit {
    _model: any;

    @Input() sections: Array<FormSectionDescriptor>;
    @Input() tableActions: Array<TableAction>;
    @Input() showCancelButton: boolean;
    @Input() showAcceptButton: boolean;
    @Input() cancelButtonCaption: string;
    @Input() inline: boolean;
    @Input() acceptButtonCaption: string;
    @Output() cancel = new EventEmitter();
    @Output() accept = new EventEmitter<any>();
    @Output() newRecordEvent = new EventEmitter<any>();
    @Output() loaded = new EventEmitter();
    @Output() tablePropertyChanged = new EventEmitter<PropertyChange>();

    get model(): any {
        return this._model;
    }

    @Input()
    set model(value: any) {
        this._model = value;
        this.setInitialValues();
    }

    form: FormGroup;
    controlValueChanges = new EventEmitter<FormControl>();
    componentsContainer = new Array<any>();

    formRepresentation: string;

    constructor(private fb: FormBuilder) {}

    ngOnInit() {
        this.form = this.createGroup();

        if (this.sections) {
            this.sections.forEach((section) => {
                section.fields = _.filter(
                    this.model.formFieldsConfig,
                    (item) => item.fieldInfo.sectionId === section.id
                );
            });
        } else {
            this.sections = new Array<FormSectionDescriptor>();
            const section = new FormSectionDescriptor(-100, "");
            this.sections.push(section);
            section.fields = this.model.formFieldsConfig;
        }

        if (!this.sections) {
        }

        this.setInitialValues();
    }

    ngAfterViewInit() {
        this.bindChanges();
        this.createSections();
        this.loaded.emit();
    }

    createSections() {
        if (!this.sections || this.sections.length === 0) {
            this.sections = new Array<FormSectionDescriptor>();
            this.sections.push(new FormSectionDescriptor(0, ""));
        }
    }

    createGroup(): FormGroup {
        if (!this.model || !this.model.formFieldsConfig) {
            return;
        }

        const group = this.fb.group({});

        // Add the properties to the form group.
        this.model.formFieldsConfig.forEach((propInfo) => {
            const info = <FormFieldConfig>propInfo;
            group.addControl(
                info.fieldInfo.name,
                this.fb.control(
                    this.model[info.fieldInfo.name],
                    info.validators
                )
            );
        });

        return group;
    }

    removeFormItem(name: string) {
        this.form.removeControl(name);
    }

    addFormItem(info: FormFieldConfig) {
        this.form.addControl(
            info.fieldInfo.name,
            this.fb.control(this.model[info.fieldInfo.name], info.validators)
        );
        if (this.sections) {
            const section = _.find(
                this.sections,
                (x) => x.id === info.fieldInfo.sectionId
            );

            if (section) {
                if (!section.fields) {
                    section.fields = _.filter(
                        this.model.formFieldsConfig,
                        (item) => item.fieldInfo.sectionId === section.id
                    );
                }

                if (section.fields) {
                    section.fields.push(info);
                }
            }
        }
    }

    bindChanges() {
        Object.keys(this.form.controls).forEach((key) => {
            const self = this;
            const control = self.form.controls[key];
            control.valueChanges.subscribe((res) => {
                self.controlValueChanges.emit(new FormControl(key, res));
                self.formRepresentation = JSON.stringify(self.form.value);
            });
        });
    }

    setInitialValues() {
        const self = this;

        if (!self.form) {
            return;
        }

        Object.keys(self.form.controls).forEach((key) => {
            const index = key.indexOf(".");

            if (index > 0) {
                const names = key.split(".");
                self.form.controls[key].setValue(
                    self.model[names[0]][names[1]]
                );
            } else {
                self.form.controls[key].setValue(self.model[key]);
            }
        });
    }

    setObservableDataSource(propertyName: string, source: any) {
        if (
            !this.componentsContainer ||
            this.componentsContainer.length === 0
        ) {
            return;
        }

        this.componentsContainer.forEach((component) => {
            const comp = <IFormField>component.instance;

            if (!comp) {
                return;
            }

            if (comp.propertyName === propertyName) {
                comp.setObservableDataSource(source);
            }
        });
    }

    performCancel() {
        this.cancel.emit();
    }

    setVisible(controlName: string, isVisible: boolean) {
        const control = _.find(this.componentsContainer, (item) => {
            return item.instance.config.fieldInfo.name === controlName;
        });

        if (control) {
            const controlInterface = control as IFormField;
            controlInterface["instance"].setVisible(isVisible);
        }
    }

    setEnabled(controlName: string, isEnabled: boolean) {
        isEnabled
            ? this.form.controls[controlName].enable()
            : this.form.controls[controlName].disable();
    }

    controlIsVisible(controlName: string, isVisible: boolean) {
        const control = _.find(this.componentsContainer, (item) => {
            return item.instance.config.fieldInfo.name === controlName;
        });

        if (control) {
            const controlInterface = control as IFormField;
            return controlInterface["instance"].isControlVisible();
        }

        return false;
    }

    setValue(controlName: string, value: any) {
        const control = this.form.controls[controlName];

        if (!control) {
            return;
        }

        control.setValue(value);
    }

    performAccept(notEmit?: boolean) {
        if (this.form.valid) {
            const data = this.parseForm();

            if (this.form.valid) {
                if (!notEmit) {
                    this.accept.emit(data);
                }

                return data;
            } else {
                this.showAllErrors();
            }
        } else {
            this.showAllErrors();
        }

        return null;
    }

    showAllErrors() {
        if (
            !this.componentsContainer ||
            this.componentsContainer.length === 0
        ) {
            return;
        }

        this.componentsContainer.forEach((component) => {
            const comp = <IFormField>component.instance;

            if (comp) {
                comp.getErrorList();
            }
        });
    }

    scrollToFirstInvalidElement() {
        const name = this.getFirstInvalidField();

        if (!name) {
            return;
        }

        $("body").animate(
            {
                scrollTop: $("[ng-reflect-name='" + name + "']")[0].offsetTop,
            },
            200
        );
    }

    getFirstInvalidField(): string {
        let name: string = null;

        Object.keys(this.form.controls).forEach((key) => {
            const control = this.form.controls[key];

            if (control.hasError && name === null) {
                name = key;
            }
        });

        return name;
    }

    getValueFromControl(name: string) {
        const control = _.find(this.componentsContainer, (item) => {
            return item.instance.config.fieldInfo.name === name;
        });

        if (control && "getValue" in control.instance) {
            const instance = <IValueAccessor>control.instance;

            if (instance) {
                return instance.getValue();
            }
        }

        return null;
    }

    parseForm() {
        const result = {};
        const self = this;

        Object.keys(self.form.controls).forEach((key) => {
            var value = self.form.controls[key].value;

            // Get the representative control.
            const control = _.find(self.componentsContainer, (item) => {
                return item.instance.config.fieldInfo.name === key;
            });
            const formField = <IFormField>control;

            if (formField && !formField["instance"].isControlVisible()) {
                value = undefined;
            } else {
                if (control && "getValue" in control.instance) {
                    const instance = <IValueAccessor>control.instance;

                    if (instance) {
                        value = instance.getValue();
                    }
                }
            }

            if (key.indexOf(".") > 0) {
                const parts = key.split(".");

                if (!result[parts[0]]) {
                    result[parts[0]] = {};
                }

                result[parts[0]][parts[1]] = value;
            } else if (key.indexOf("_") > 0) {
                const parts = key.split("_");

                const attributeName = parts[0];
                const index = +parts[1];

                // Get the options configuration using the base attribute.
                var element = _.find(
                    self.model.formFieldsConfig,
                    (item) => item.fieldInfo.name === attributeName
                );

                if (
                    !result[attributeName] ||
                    (result[attributeName] &&
                        result[attributeName].length === 0)
                ) {
                    result[attributeName] = self.model[attributeName];
                }

                result[attributeName][index][
                    element.inlineOptions.valuePropertyName
                ] = value;
            } else {
                result[key] = value;
            }
        });

        return result;
    }

    newRecordEventHandler() {
        this.newRecordEvent.emit(undefined);
    }
}
